import { useCallback, useEffect, useRef } from 'react';
import { renderToString } from 'react-dom/server';
import styled from '@emotion/styled';
import c3 from 'c3';
import type { ChartConfiguration } from 'c3';
import { tokens } from '@hotjar/hotsauce';
import { generateId } from '@hotjar/webapp/lib/componentId';
import { useTranslation, formatPercentage } from '@hotjar/translations';
import { Tooltip } from './Tooltip';

const Container = styled.div`
  min-height: 100%;
`;
interface AnswersBarChartProps {
  chartData: { dataset: number[]; labels: string[] };
  highestValue: number;
  usePercentages?: boolean;
}

export function AnswersBarChart({
  chartData,
  highestValue,
  usePercentages = false,
}: AnswersBarChartProps): JSX.Element | null {
  const chartIdRef = useRef(generateId('answers-chart'));
  const { i18n } = useTranslation('feedback');

  const getYAxisConfig = useCallback(() => {
    return usePercentages
      ? {
          max: 100,
          tick: {
            count: 6,
            format: (d: number) => formatPercentage(d, i18n),
          },
        }
      : {
          // There are 7 ticks. We calculate an axis max based on 6 equal steps.
          max: Math.ceil(highestValue / 6) * 6,
          tick: {
            count: 7,
            format: (d: number) => `${Math.ceil(d)}`,
          },
        };
  }, [highestValue, i18n, usePercentages]);

  useEffect(() => {
    const config: ChartConfiguration = {
      axis: {
        x: {
          type: 'category',
          categories: chartData.labels,
        },
        y: {
          padding: {
            bottom: 0,
            top: 0,
          },
          ...getYAxisConfig(),
        },
      },
      bindto: `#${chartIdRef.current}`,
      data: {
        colors: {
          answers: tokens.color.azure,
        },
        columns: [['answers' as string | number].concat(chartData.dataset)],
        type: 'bar',
      },
      bar: {
        width: {
          ratio: 0.8,
        },
      },
      grid: {
        x: {
          show: true,
        },
        y: {
          show: true,
        },
      },
      legend: {
        show: false,
      },
      padding: {
        right: 0,
        bottom: 16,
        left: usePercentages ? 40 : undefined,
      },
      tooltip: {
        grouped: true,
        contents(d) {
          return renderToString(
            Tooltip({
              value: usePercentages ? formatPercentage(d[0].value, i18n) : d[0].value,
            }),
          );
        },
      },
    };

    c3.generate(config);
  }, [chartData, getYAxisConfig, highestValue, i18n, usePercentages]);

  return <Container id={chartIdRef.current} />;
}
